import { FLAGS } from '@iheartjane/feature-flags';
import type { CardCarouselProps } from '@iheartjane/menu';
import { CardCarousel, SpecialCard, getSpecialSlug } from '@iheartjane/menu';
import type {
  Nullable,
  ReservationModeLabels,
  SpecialType,
} from '@iheartjane/types';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackStoreSpecialTap } from '@jane/bloom/services/tracking/track';
import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { ComplianceLanguage } from '@jane/bloom/types/schemas/roots/complianceLanguage';

import { SpecialCard as OldSpecialCard } from './specialCard';

export type SpecialsRowProps = {
  className?: string;
  complianceLanguage?: Nullable<ComplianceLanguage>;
  displayName?: Nullable<string>;
  mode?: CardCarouselProps['mode'];
  reservationModeLabels?: ReservationModeLabels;
  specialsRedesignMenu?: boolean;
  storeSpecials: StoreSpecial[];
};

export function SpecialsRow({
  className,
  complianceLanguage,
  displayName,
  mode = 'ideal-width',
  reservationModeLabels,
  specialsRedesignMenu,
  storeSpecials,
}: SpecialsRowProps) {
  const generatePath = useFullDestination();
  const { featureFlags } = useRootLoaderData();
  const extendedDealsLabelsEnabled = featureFlags[FLAGS.c1ExtendedDealsLabels];

  if (storeSpecials.length === 0) {
    return null;
  }

  const rowName = displayName ?? 'Deals';

  return (
    <CardCarousel
      className={className}
      cardGap={24}
      cardGapMobile={16}
      cardHeight={240}
      cardHeightMobile={240}
      cardWidth={420}
      cardWidthMobile={270}
      mode={mode}
      name={rowName}
    >
      {storeSpecials.map((special, index) => (
        <CardCarousel.Card key={special.id}>
          {specialsRedesignMenu ? (
            <SpecialCard
              key={special.id}
              customSpecialBadge={special?.custom_badge}
              discountAmount={special?.discount_amount}
              discountType={special?.discount_type}
              extendedDealsLabelsEnabled={extendedDealsLabelsEnabled}
              imageSrc={special?.photo?.urls?.original}
              isPremium
              reservationModeLabels={reservationModeLabels}
              reservationModes={special?.reservation_modes}
              specialType={special?.special_type as SpecialType}
              storeComplianceLanguageSettings={complianceLanguage}
              title={special.title}
              to={generatePath(
                `/deals/${special.id}/${getSpecialSlug(special.title)}`
              )}
              onClick={() =>
                trackStoreSpecialTap({
                  source: 'specials row',
                  specialId: special.id,
                  specialRankOnList: index + 1,
                  specialType: special.special_type as SpecialType,
                })
              }
            />
          ) : (
            <OldSpecialCard
              special={special}
              complianceLanguage={complianceLanguage}
            />
          )}
        </CardCarousel.Card>
      ))}
    </CardCarousel>
  );
}
